import React, { useRef, useState } from 'react';
import { Box, Typography, Loader, Badge, Button, TextArea } from '@lucid/lego';
import {
  useCreateNote,
  useSearchNotes,
  transformComments,
  type NoteEntityType,
} from '../../api/services/lupp/notes';
import { formatDateTime } from '../../utils/tools';
import styles from './Comments.module.scss';

export interface CommentsProps {
  /** Entity identifier for the notes (e.g., case ID) */
  entityId: string;
  /** Entity type for the notes API */
  entityType: NoteEntityType;
  /** Theme for the component */
  theme: 'light' | 'dark';
  /** Whether to fetch notes (usually tied to parent visibility) */
  enabled?: boolean;
  /** Google Analytics component identifier */
  gaComponent?: string;
  /** Callback to receive comment count (useful for parent components displaying count) */
  onCommentCountChange?: (count: number) => void;
}

/**
 * Shared comments component for displaying and posting notes/comments.
 * Used across CustomerCare, ServiceCenter, and other pages with case details.
 * This component is decoupled from any wrapper (like Accordion) to be reusable in different contexts.
 */
const Comments: React.FC<CommentsProps> = ({
  entityId,
  entityType,
  theme,
  enabled = true,
  gaComponent = 'comments-section',
  onCommentCountChange,
}) => {
  const { data: notesData, isLoading: notesLoading } = useSearchNotes(
    entityId,
    entityType,
    enabled && !!entityId
  );
  const createNoteMutation = useCreateNote();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Transform comments from API response using centralized transformer
  const comments = transformComments(notesData?.data);

  // Notify parent of comment count changes
  React.useEffect(() => {
    onCommentCountChange?.(comments.length);
  }, [comments.length, onCommentCountChange]);

  const handlePostComment = async () => {
    const commentText = textAreaRef.current?.value?.trim() || '';
    if (commentText && entityId) {
      setIsSubmitting(true);
      try {
        await createNoteMutation.mutateAsync({
          noteText: commentText,
          authorName: 'Current User', // TODO: Get from auth context
          authorRole: 'Partner', // TODO: Get from auth context
          entityIdentifier: entityId,
          entityType,
        });
        // Clear textarea after successful post
        if (textAreaRef.current) {
          textAreaRef.current.value = '';
        }
      } catch (error) {
        console.error('Failed to post comment:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && textAreaRef.current?.value?.trim() && !isSubmitting) {
      e.preventDefault();
      handlePostComment();
    }
  };

  return (
    <Box className={styles.commentsContainer}>
      {/* Existing Comments */}
      {notesLoading ? (
        <Box pt={24} pb={24} className={styles.loadingContainer}>
          <Loader size={24} />
        </Box>
      ) : comments.length === 0 ? (
        <Box pt={16} pb={16}>
          <Typography variant="body" size="small" theme={theme} color="gray-5">
            No comments yet. Be the first to add a comment.
          </Typography>
        </Box>
      ) : (
        comments.map((comment) => (
          <Box key={comment.id} className={styles.commentItem}>
            <Box className={styles.avatar}>
              <Typography variant="body" size="small" theme={theme} color="white">
                {comment.initials}
              </Typography>
            </Box>
            <Box className={styles.commentContent}>
              <Box className={styles.commentHeader}>
                <Typography variant="body" size="small" theme={theme} color="white">
                  {comment.author}
                </Typography>
                <Badge
                  label={comment.role}
                  color={comment.role === 'Lucid' ? 'informative' : 'warning'}
                  type="secondary"
                  theme={theme}
                />
                <Typography variant="body" size="small" theme={theme} color="gray-5">
                  {formatDateTime(comment.timestamp)}
                </Typography>
              </Box>
              <Typography variant="body" size="small" theme={theme} color="white">
                {comment.message}
              </Typography>
            </Box>
          </Box>
        ))
      )}

      {/* Add New Comment */}
      <Box className={styles.addCommentSection}>
        <Box className={styles.youAvatar}>
          <Typography variant="body" size="small" theme={theme} color="white">
            You
          </Typography>
        </Box>
        <Box className={styles.commentInputWrapper}>
          <Box className={styles.commentInputArea} onKeyDown={handleKeyDown}>
            <TextArea
              ref={textAreaRef}
              name="newComment"
              placeholder="Add a comment..."
              theme={theme}
              controlled={false}
              rows={3}
              resize="vertical"
              showValidation={false}
              className={styles.commentTextarea}
            />
          </Box>
          <Box className={styles.commentActions}>
            <Button
              label={isSubmitting ? 'Posting...' : 'Post Comment'}
              type="primary"
              size="small"
              theme={theme}
              handleClick={handlePostComment}
              disabled={isSubmitting}
              gaComponent={gaComponent}
              dataTestId="post-comment-button"
              className={styles.postCommentButton}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Comments;
